import { ChevronLeftIcon, UserIcon } from '@assets/icons';
import { useLocation } from '@gatsbyjs/reach-router';
import { sideNavExpandedAtom } from '@hooks/sideNavAtoms';
import { Stack } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useWorkspaceContext } from '@navigation/WorkspaceContext';
import { navigate } from 'gatsby';
import { useAtom } from 'jotai';
import { NavDivider } from './NavDivider';
import { OrganizationNav } from './OrganizationNav';
import { SideNavButton } from './SideNavButton';
import { WorkspaceNav } from './WorkspaceNav';

const getSelectedTabFromLocation = ({
  pathname,
  serviceOnly,
  workspaceId,
}: {
  pathname: string;
  serviceOnly: boolean;
  workspaceId: string;
}) => {
  // Profile context
  if (pathname.includes('profile')) {
    return 'profile';
  }

  // Workspace context
  if (workspaceId && pathname.includes('workspaces') && !serviceOnly) {
    if (pathname.includes('volumes')) {
      return 'workspace-volumes';
    }
    if (pathname.includes('graphs')) {
      return 'workspace-graphs';
    }
    if (pathname.includes('jobs')) {
      return 'workspace-jobs';
    }
    if (pathname.includes('datasets')) {
      return 'workspace-datasets';
    }
    if (pathname.includes('experiments')) {
      return 'workspace-experiments';
    }
    return 'workspace-home';
  }

  // Organization context

  // Examples if first to short circuit because example subroutes include other route names
  if (pathname.includes('examples')) {
    return 'examples';
  }
  if (pathname.includes('sample-gallery')) {
    return 'sample-gallery';
  }
  if (pathname.includes('workspaces')) {
    return 'workspaces';
  }
  if (pathname.includes('remote-sessions')) {
    return 'remote-sessions';
  }
  if (pathname.includes('channels')) {
    return 'channels';
  }
  if (pathname.includes('resources')) {
    return 'resources';
  }
  if (pathname.includes('/services')) {
    return 'resources';
  }
  if (pathname.includes('volumes')) {
    return serviceOnly ? 'resources' : 'volumes';
  }
  if (pathname.includes('mcp-configs')) {
    return 'resources';
  }
  if (pathname.includes('skills')) {
    return 'resources';
  }
  if (pathname.includes('rules')) {
    return 'rules';
  }
  if (pathname.includes('gan-models')) {
    return 'gan-models';
  }
  if (pathname.includes('annotation-maps')) {
    return 'annotation-maps';
  }
  if (pathname.includes('settings')) {
    return 'settings';
  }
  if (pathname.includes('servers')) {
    return 'servers';
  }
  return 'home';
};

export const SideNav = () => {
  const location = useLocation();
  const { workspaceId } = useWorkspaceContext();
  const { organization } = useOrganizationContext();
  const selectedTab = getSelectedTabFromLocation({
    pathname: location?.pathname,
    serviceOnly: organization?.serviceOnly ?? false,
    workspaceId,
  });
  const [isExpanded, setIsExpanded] = useAtom(sideNavExpandedAtom);
  const toggleExpanded = () => setIsExpanded(!isExpanded);
  return (
    <Stack
      component="nav"
      sx={{
        backgroundColor: 'header.secondary',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        pt: 2,
        overflow: 'auto',
        width: isExpanded ? '225px' : '64px',
        transition: 'width 0.3s ease-in-out',
        pl: 1,
      }}
    >
      <Stack width="100%">
        <OrganizationNav selectedTab={selectedTab} />
        <WorkspaceNav selectedTab={selectedTab} />
      </Stack>

      <Stack sx={{ width: '100%' }}>
        <NavDivider />
        <SideNavButton
          sx={{ minHeight: '44px' }}
          Icon={UserIcon}
          label="Profile"
          onClick={() => navigate('/profile')}
          selected={selectedTab === 'profile'}
        />
        <SideNavButton
          sx={{ minHeight: '44px' }}
          Icon={ChevronLeftIcon}
          iconProps={{
            double: true,
            style: { transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)' },
          }}
          label={isExpanded ? 'Collapse' : 'Expand'}
          onClick={toggleExpanded}
        />
      </Stack>
    </Stack>
  );
};
